import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface VoiceOption {
  voice: SpeechSynthesisVoice;
  label: string;
}

@Injectable({
  providedIn: 'root'
})
export class TextToSpeechService {
  private synth: SpeechSynthesis;
  private utterance: SpeechSynthesisUtterance;
  private voices: SpeechSynthesisVoice[] = [];
  private selectedVoice?: SpeechSynthesisVoice;
  private endCallback: () => void = () => {
    console.log()
  };
  private voicesLoaded = new BehaviorSubject<boolean>(false);

  constructor() {
    this.synth = window.speechSynthesis;
    this.utterance = new SpeechSynthesisUtterance();

    // Load voices when available
    if (this.synth.onvoiceschanged !== undefined) {
      this.synth.onvoiceschanged = () => {
        this.voices = this.synth.getVoices();
        this.voicesLoaded.next(true);
      };
    }

    // Initial load of voices
    setTimeout(() => {
      this.voices = this.synth.getVoices();
      this.voicesLoaded.next(true);
    }, 100);
  }

  public getVoicesLoadedState(): Observable<boolean> {
    return this.voicesLoaded.asObservable();
  }

  public getAvailableVoices(): VoiceOption[] {
    return this.voices.map(voice => ({
      voice,
      label: `${voice.name} (${voice.lang})`
    }));
  }

  public setVoice(voice: SpeechSynthesisVoice): void {
    this.selectedVoice = voice;
  }

  /**
   * Speak the given text. A fresh utterance is created per call so that each
   * playback owns its own end/error handler — this prevents a previously
   * playing message's callback from clearing the state of a newly started one.
   * @param text  text to read aloud
   * @param onEnd optional callback fired when this specific playback ends or errors
   */
  public speak(text: string, onEnd?: () => void): void {
    // Cancel anything currently playing (fires the previous utterance's handler)
    this.stop();

    const utterance = new SpeechSynthesisUtterance(text);
    if (this.selectedVoice) {
      utterance.voice = this.selectedVoice;
    }

    const callback = onEnd ?? this.endCallback;
    utterance.onend = () => callback();
    utterance.onerror = () => callback();

    this.utterance = utterance;
    this.synth.speak(utterance);
  }

  public stop(): void {
    this.synth.cancel();
  }

  /** True while the synthesizer is actively speaking. */
  public isSpeaking(): boolean {
    return this.synth.speaking;
  }

  public onEnd(callback: () => void): void {
    this.endCallback = callback;
  }
}
